import React, { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import styled, { StyledProps } from 'styled-components'
import { helpersColor } from '../../../helpers'
import { useTranslation } from 'react-i18next'

type FileDropzoneDragStatus = 'none' | 'active' | 'accept' | 'reject' | 'invalid-count'
type ContainerProps = StyledProps<{ dragStatus: FileDropzoneDragStatus }>

const getContainerAccentColor = (props: ContainerProps): string => {
  if (props.dragStatus === 'none') {
    return props.theme.color.primary500
  }
  if (props.dragStatus === 'active') {
    return props.theme.color.primary600
  }
  if (props.dragStatus === 'accept') {
    return props.theme.color.accentApproving
  }
  if (props.dragStatus === 'reject') {
    return props.theme.color.accentDestructive
  }
  if (props.dragStatus === 'invalid-count') {
    return props.theme.color.accentDestructive
  }
  return props.theme.color.primary500
}

const Container = styled.div<ContainerProps>`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: 2px dashed ${getContainerAccentColor};
  background-color: ${props => helpersColor.alphaColor(getContainerAccentColor(props), 0.1)};
`

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`

const ContentMessageAccept = styled.div`
  color: ${props => props.theme.color.accentApproving};
`
const ContentMessageReject = styled.div`
  color: ${props => props.theme.color.accentDestructive};
`
const ContentMessageDefault = styled.div`
  color: ${props => props.theme.color.onPrimaryDark};
`
const DropLink = styled.span`
  color: ${props => props.theme.color.primary500};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const DropzoneInnerContent: React.FC<{
  dragStatus: FileDropzoneDragStatus
  accept: string | string[]
  maxFiles: number
}> = ({ dragStatus, accept, maxFiles }) => {
  let content: React.ReactNode = null
  const { t } = useTranslation('ui')

  switch (dragStatus) {
    case 'accept':
      content = <ContentMessageAccept>{t('FileDropzone.dragStatus.accept')}</ContentMessageAccept>
      break
    case 'reject':
      const formats = Array.isArray(accept) ? accept.join(', ') : accept
      content = <ContentMessageReject>{t('FileDropzone.dragStatus.reject', { formats })}</ContentMessageReject>
      break
    case 'invalid-count':
      content = (
        <ContentMessageReject>
          {t('FileDropzone.dragStatus.invalid-count', { fileCount: maxFiles })}
        </ContentMessageReject>
      )
      break
    default:
      content = (
        <ContentMessageDefault>
          {t('FileDropzone.dropText')}
          <DropLink>{t('FileDropzone.dropLink')}</DropLink>
        </ContentMessageDefault>
      )
  }

  return <ContentContainer>{content}</ContentContainer>
}

export const FileDropzone: React.FC<{
  minSize?: number
  maxSize?: number
  maxFiles?: number
  disabled?: boolean
  onDrop(files: File[]): void
  accept: string | string[]
  render?(renderOptions: { dragStatus: FileDropzoneDragStatus; maxFiles: number; draggedFilesCount: number })
}> = ({ onDrop, render, accept, maxSize, minSize, maxFiles = 1, disabled = false }) => {
  const onDropAccepted = useCallback(
    files => {
      onDrop(files)
    },
    [onDrop],
  )

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, draggedFiles } = useDropzone({
    onDropAccepted,
    accept,
    // validator: file => {
    //   console.log(file)
    //   return null
    // },
    maxSize,
    minSize,
    maxFiles,
    disabled,
    preventDropOnDocument: true,
  })

  let dragStatus: FileDropzoneDragStatus = 'none'
  if (isDragActive) {
    dragStatus = 'active'
  }
  if (isDragAccept) {
    dragStatus = 'accept'
  }
  if (isDragReject) {
    dragStatus = 'reject'
  }
  if (maxFiles < draggedFiles.length) {
    dragStatus = 'invalid-count'
  }

  return (
    <Container {...getRootProps()} dragStatus={dragStatus}>
      <input {...getInputProps()} />
      {render ? (
        render({ dragStatus, maxFiles, draggedFilesCount: draggedFiles.length })
      ) : (
        <DropzoneInnerContent dragStatus={dragStatus} maxFiles={maxFiles} accept={accept} />
      )}
    </Container>
  )
}
